/**
 * ICONE DELLE AZIONI — risolve il token che il catalogo ALLORA dichiara nel componente da disegnare.
 *
 * Il backend NON manda un'icona: manda un TOKEN astratto (`AutomationActionType.icon`, es. `mail`),
 * che non è una classe CSS e non è il percorso di un file. Il vocabolario dei token è di qui, e la
 * SPI lo dice esplicitamente. Stessa mossa delle etichette — il catalogo dichiara cosa intende, il
 * client decide come si disegna — e stessa convenzione del campo `icon` di `MenuItem` /
 * `EntityTypeDescriptor` in `@crm/contracts`, che `ICON_MAP` in `Sidebar.tsx` risolve allo stesso modo.
 *
 * **Un token sconosciuto non è un errore**: si ricade su {@link DEFAULT_ACTION_ICON}. È questo a
 * rendere vero il disaccoppiamento, non il token in sé: un plugin dichiara la propria icona senza
 * che il core sappia nulla di lui, e il core non contiene una mappa dei codici azione dei plugin.
 * Il giorno che un modulo cliente registra una propria azione, qui non si tocca niente.
 *
 * Mappa LOCALE alle azioni, e non l'`ICON_MAP` della Sidebar (che è privato lì): scelta presa per
 * non toccare un componente che funziona. Il prezzo è che i due vocabolari possono divergere — se un
 * giorno pesano, si estrae un registry condiviso.
 *
 * Perché una mappa scritta a mano invece di una lookup dinamica su `@mui/icons-material`: il barrel
 * (`import * as Icons`) tira nel bundle l'intero set, e un import dinamico con nome interpolato
 * costringe Vite a prevedere ogni match possibile, che è lo stesso risultato. Qui entrano solo le
 * icone elencate — ed è anche il motivo per cui
 * il vocabolario è chiuso e serve il fallback.
 */

import AutorenewIcon from '@mui/icons-material/Autorenew';
import BoltIcon from '@mui/icons-material/Bolt';
import EmailIcon from '@mui/icons-material/Email';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';

/** Token dell'icona generica. SYNC con `AutomationActionType.DEFAULT_ICON` (crm-automation-api). */
export const DEFAULT_ACTION_ICON = 'default';

/** Stesso tipo della `ICON_MAP` di `Sidebar.tsx`, che risolve i token allo stesso modo. */
const ACTION_ICON: Record<string, React.ElementType> = {
  // Un concetto e non un'azione: ogni azione svolta da un agente IA, di prodotto o di un plugin.
  assistant: SmartToyIcon,
  mail: EmailIcon,
  task: TaskAltIcon,
  trace: TroubleshootIcon,
  update: AutorenewIcon,
  [DEFAULT_ACTION_ICON]: BoltIcon,
};

/** Il componente icona per un token del catalogo; generica se il token non è fra quelli noti. */
export function actionIcon(token: string): React.ElementType {
  return ACTION_ICON[token] ?? ACTION_ICON[DEFAULT_ACTION_ICON];
}
